import { SearchSmall } from '@blueprint/icons';
import triggerTracking from '@blueprint/tracking';
import { SearchPageType, SearchSource } from '@zoopla/listing-search-context';
import {
    getBedsMax,
    getBedsMin,
    getDuration,
    getIsRetirementHome,
    getPriceFrequency,
    getPriceMax,
    getPriceMin,
    getPropertySubType,
    getPropertyType,
    getQ,
    getRadius,
    getSearchPath,
    getTransportType
} from '@zoopla/listing-search/filters/modal';
import { LocationSearchInput } from '@zoopla/listing-search/input';
import { useRouter } from 'next/router';
import { FC, MouseEvent, useState } from 'react';
import { PropertyType } from '../SearchInitiation/SelectNewHomesDevelopersPropertyType';
import { formatLocation } from '../utils/formatLocation';
import { formatSearchPath } from '../utils/formatSearchPath';
import { getUpdatedQuery } from '../utils/getUpdatedQuery';
import {
    LocationFilterWrapper,
    MobileSectionHeadline,
    UpdateButtonWithIcon
} from './SearchInitiationMobile.styles';
import {
    BedsMin,
    PriceMax,
    SelectBedsPriceForSale
} from './SelectBedsPriceForSale';
import { SelectBedsToRent } from './SelectBedsToRent';
import { Duration, SelectMaxTravelTime } from './SelectMaxTravelTime';
import { SelectNewHomesDevelopersPropertyType } from './SelectNewHomesDevelopersPropertyType';
import { PriceFrequency, SelectPriceToRent } from './SelectPriceToRent';
import { PropertySubType, SelectPropertyType } from './SelectPropertyType';
import { Radius, SelectRadius } from './SelectRadius';
import { SelectTransportMode, TransportType } from './SelectTransportMode';

type Tab = SearchPageType | 'house-prices';

const PRICE_RANGE = 'Price range';
const BEDROOMS = 'Bedrooms';
const EVENT = 'GAEvent';
const CATEGORY = 'search_filters';

type SearchInitiationMobileProps = {
    showRadius?: boolean;
    updateResultsText: string;
    searchType: Tab;
    eventCategory: 'homepage' | 'search results' | 'new_homes';
    searchSource: SearchSource;
};

export const SearchInitiationMobile: FC<
    React.PropsWithChildren<SearchInitiationMobileProps>
> = ({
    showRadius = true,
    updateResultsText,
    searchType,
    eventCategory,
    searchSource
}) => {
    const { query, push, pathname } = useRouter();

    const [formValue, updateFormValue] = useState({
        beds_max: getBedsMax(query),
        beds_min: getBedsMin(query),
        duration: getDuration(query),
        is_retirement_home: getIsRetirementHome(query),
        price_frequency: getPriceFrequency(query),
        price_max: getPriceMax(query),
        price_min: getPriceMin(query),
        property_sub_type: getPropertySubType(query),
        property_type: getPropertyType(query),
        q: getQ(query),
        radius: getRadius(query),
        transport_type: getTransportType(query)
    });

    const searchPath = getSearchPath(query);

    const isTravelTimeSearch = Boolean(
        getDuration(query) && getTransportType(query)
    );

    const formattedSearchPath = formatSearchPath(searchPath);

    const [locationText, setLocationText] = useState(
        formValue.q || formattedSearchPath
    );
    const [identifier, setIdentifier] = useState(formatLocation(searchPath));

    const [hasSubmittedLocationSearch, setHasSubmittedLocationSearch] =
        useState(false);

    const isForSaleSearch =
        searchType === 'for-sale' || searchType === 'new-homes';
    const isNewHomesDevelopersSearch = searchSource === 'new-homes-dev';

    const viewType = pathname.includes('/map/') ? 'map' : 'list';

    const handleSearchProperties = async () => {
        if (!locationText) {
            setHasSubmittedLocationSearch(true);
            return;
        }

        const eventLabel = locationText
            ? `${searchType}-success`
            : `${searchType}-empty-search`;

        triggerTracking({
            event: 'GAEvent',
            eventCategory,
            eventAction: 'search',
            eventLabel
        });

        const updatedQuery = getUpdatedQuery({
            query,
            formValue,
            isNewHomesDevelopersSearch,
            identifier,
            searchSource,
            viewType
        });

        await push({
            pathname: pathname,
            query: {
                ...updatedQuery
            }
        });
    };

    const handlePressUpdate = (evt: MouseEvent<HTMLButtonElement>) => {
        evt.preventDefault();
        evt.stopPropagation();

        triggerTracking({
            event: EVENT,
            eventCategory: CATEGORY,
            eventAction: 'click',
            eventLabel: 'quick_search_bar_update'
        });

        handleSearchProperties();
    };

    const showError = hasSubmittedLocationSearch && !locationText;

    return (
        <>
            <LocationFilterWrapper>
                <LocationSearchInput
                    id="filters-location-mobile"
                    onLocationInputKeyDown={handleSearchProperties}
                    onlyDisplayRemoveIconWhenActive={false}
                    value={locationText as string}
                    onLocationSelect={({ identifier, q }) => {
                        updateFormValue((oldFormValue) => ({
                            ...oldFormValue,
                            q: q
                        }));
                        setIdentifier(identifier);
                    }}
                    handleInputTextChange={(value) => {
                        updateFormValue((oldFormValue) => ({
                            ...oldFormValue,
                            q: ''
                        }));
                        setIdentifier(value);
                        setLocationText(value);
                    }}
                    showError={showError}
                />
            </LocationFilterWrapper>

            {isTravelTimeSearch ? (
                <>
                    <SelectMaxTravelTime
                        value={formValue.duration as Duration}
                        onChange={(duration) => {
                            updateFormValue((oldFormValue) => ({
                                ...oldFormValue,
                                duration: duration
                            }));
                        }}
                    />
                    <SelectTransportMode
                        value={formValue.transport_type as TransportType}
                        onChange={(transportType) => {
                            updateFormValue((oldFormValue) => ({
                                ...oldFormValue,
                                transport_type: transportType
                            }));
                        }}
                    />{' '}
                </>
            ) : showRadius ? (
                <SelectRadius
                    value={formValue.radius as Radius}
                    onChange={(radius) => {
                        updateFormValue((oldFormValue) => ({
                            ...oldFormValue,
                            radius: radius
                        }));
                    }}
                />
            ) : null}

            {isForSaleSearch ? (
                <SelectBedsPriceForSale
                    value={{
                        bedsMin: formValue.beds_min as BedsMin,
                        priceMax: formValue.price_max as PriceMax
                    }}
                    onChange={({ bedsMin, priceMax }) => {
                        updateFormValue((oldFormValue) => ({
                            ...oldFormValue,
                            beds_min: bedsMin,
                            price_max: priceMax
                        }));
                    }}
                />
            ) : (
                <>
                    <MobileSectionHeadline>{PRICE_RANGE}</MobileSectionHeadline>
                    <SelectPriceToRent
                        value={{
                            priceMax: formValue.price_max as PriceMax,
                            priceFrequency: (formValue.price_frequency ||
                                'per_month') as PriceFrequency
                        }}
                        onChange={({ priceMax, priceFrequency }) => {
                            updateFormValue((oldFormValue) => ({
                                ...oldFormValue,
                                price_max: priceMax,
                                price_frequency: priceFrequency
                            }));
                        }}
                    />

                    <MobileSectionHeadline>{BEDROOMS}</MobileSectionHeadline>
                    <SelectBedsToRent
                        value={formValue.beds_min as BedsMin}
                        onChange={(bedsMin) => {
                            updateFormValue((oldFormValue) => ({
                                ...oldFormValue,
                                beds_min: bedsMin
                            }));
                        }}
                    />
                </>
            )}

            {isNewHomesDevelopersSearch ? (
                <SelectNewHomesDevelopersPropertyType
                    value={formValue.property_type as PropertyType}
                    onChange={(propertyType) => {
                        updateFormValue((oldFormValue) => ({
                            ...oldFormValue,
                            property_type: propertyType
                        }));
                    }}
                />
            ) : (
                <SelectPropertyType
                    value={{
                        propertyType: formValue.property_type as PropertyType,
                        propertySubType:
                            formValue.property_sub_type as PropertySubType
                    }}
                    onChange={({ propertySubType, propertyType }) => {
                        updateFormValue((oldFormValue) => ({
                            ...oldFormValue,
                            property_sub_type: propertySubType,
                            property_type: propertyType
                        }));
                    }}
                />
            )}

            <UpdateButtonWithIcon
                isFullWidth
                type="button"
                onClick={handlePressUpdate}
                data-testid="update-results-button"
                title="Update"
            >
                <SearchSmall />
                {updateResultsText}
            </UpdateButtonWithIcon>
        </>
    );
};
